import type { AssessmentResult, PlainHeard, WordErrorType, WordScore } from '../types.ts';
import { alignTranscript } from './align.ts';
import { normalize } from './text.ts';

/**
 * Bramka treści: łączy dwa niezależne rozpoznania tej samej wypowiedzi.
 *
 *  1. Ocena wymowy Azure ZE zdaniem wzorcowym (scripted) — dobra w wymowie głosek i w wykrywaniu ucięcia,
 *     ale „przyciąga” rozpoznany tekst do wzorca (potrafi usłyszeć „I want food” w „I went food”).
 *  2. Zwykłe rozpoznawanie mowy BEZ wzorca (transkrypt) — słyszy, co naprawdę zostało powiedziane.
 *
 * Wynik to jeden AssessmentResult, dla którego zwykłe `decide()` daje to samo, co „oba rozpoznania muszą się zgodzić”:
 * kompletność i dokładność to minimum z obu, a ocena każdego słowa to minimum z obu. Dzięki temu UI (kolory słów,
 * „Popracuj nad: …”) działa bez zmian.
 */

const RANK: Record<WordErrorType, number> = { None: 0, Insertion: 1, Mispronunciation: 2, Omission: 3 };
const worse = (a: WordErrorType, b: WordErrorType): WordErrorType => (RANK[a] >= RANK[b] ? a : b);

export function combineWithTranscript(reference: string, scripted: AssessmentResult, plain: string | PlainHeard): AssessmentResult {
  const heard: PlainHeard = typeof plain === 'string' ? { text: plain } : plain;
  const plainText = heard.text;
  const aligned = alignTranscript(reference, plainText);
  const sw = scripted.words.filter((w) => w.error !== 'Insertion');
  const aw = aligned.words.filter((w) => w.error !== 'Insertion');
  const sameShape = sw.length === aw.length;

  const used = new Set<WordScore>();
  const words: WordScore[] = aw.map((a, i) => {
    const s: WordScore | undefined = sameShape ? sw[i] : sw.find((x) => !used.has(x) && normalize(x.word) === a.word);
    if (s) used.add(s);
    if (!s) return a; // nie umiemy sparować — zostaje ocena z transkryptu
    const merged: WordScore = { ...s, accuracy: Math.min(s.accuracy, a.accuracy), error: worse(s.error, a.error) };
    if (a.accuracy < s.accuracy && s.azureAccuracy === undefined) merged.azureAccuracy = s.accuracy; // pokaż, że ocenę obniżył transkrypt
    return merged;
  });

  // słowa dodatkowe (Insertion) z obu źródeł, bez duplikatów
  const seen = new Set<string>();
  for (const w of [...scripted.words, ...aligned.words]) {
    if (w.error !== 'Insertion') continue;
    const key = normalize(w.word);
    if (seen.has(key)) continue;
    seen.add(key);
    words.push(w);
  }

  const result: AssessmentResult = {
    source: 'azure',
    recognizedText: plainText,
    scriptedText: scripted.recognizedText,
    completeness: Math.min(scripted.completeness, aligned.completeness),
    accuracy: Math.min(scripted.accuracy, aligned.accuracy),
    words,
  };
  result.transcriptExact = aligned.words.length > 0 && aligned.words.every((w) => w.error === 'None');
  if (heard.confidence !== undefined) result.transcriptConfidence = heard.confidence;
  if (heard.ties !== undefined) result.transcriptTies = heard.ties;
  if (heard.alternatives !== undefined && heard.alternatives.length > 0) result.transcriptAlternatives = heard.alternatives;
  if (scripted.fluency !== undefined) result.fluency = scripted.fluency;
  if (scripted.prosody !== undefined) result.prosody = scripted.prosody;
  if (scripted.pronScore !== undefined) result.pronScore = scripted.pronScore;
  if (scripted.note) result.note = scripted.note;
  return result;
}
